import type {
  MediaAttachmentDto,
  MediaAttachmentFileDto,
  MediaAttachmentRoleCodeDto,
  MediaAttachmentsOwnerContractDto,
  MediaAttachmentsOwnerDto,
  MediaOwnerAttachmentRequest,
  MediaRenditionDto,
} from '@core/api/contracts';
import type {
  AttachmentFileViewModel,
  AttachmentViewModel,
  MediaAttachmentsOwnerViewModel,
  RenditionViewModel,
} from '../models/media-attachments-owner.view-model';
import { resolveReadyImagePreviewUrl } from '../utils/media-preview.util';

export function toMediaAttachmentsOwnerViewModel(
  response: MediaAttachmentsOwnerContractDto
): MediaAttachmentsOwnerViewModel {
  return {
    defaultMediaFolderId: response.defaultMediaFolderId,
    attachments: response.attachments.map(toRoleCodeAttachmentViewModel),
    currentVersions: response.currentVersions,
  };
}

export function toEmbeddedMediaAttachmentsOwnerViewModel(
  response: MediaAttachmentsOwnerDto
): MediaAttachmentsOwnerViewModel {
  return {
    defaultMediaFolderId: response.defaultMediaFolderId,
    attachments: response.attachments.map(toEmbeddedAttachmentViewModel),
    currentVersions: {},
  };
}

export function toMediaOwnerAttachmentRequest(
  attachment: AttachmentViewModel
): MediaOwnerAttachmentRequest {
  return {
    fileId: attachment.fileId,
    roleCode: attachment.roleCode ?? attachment.role,
    sortOrder: attachment.sortOrder,
    title: attachment.title ?? undefined,
    altText: attachment.altText ?? undefined,
    caption: attachment.caption ?? undefined,
    publish: attachment.publish,
  };
}

export function resolveMediaAttachmentLogoUrl(
  attachments: AttachmentViewModel[]
): string | undefined {
  const logo = attachments.find((attachment) => (attachment.roleCode ?? attachment.role) === 'LOGO');
  if (!logo) {
    return undefined;
  }

  return resolveReadyImagePreviewUrl(logo.file, {
    preferredRenditionUrl: logo.file.renditions.find((rendition) => rendition.kind === 'W1280')?.previewUrl,
  }) ?? undefined;
}

function toRoleCodeAttachmentViewModel(
  attachment: MediaAttachmentRoleCodeDto
): AttachmentViewModel {
  return {
    attachmentId: attachment.attachmentId,
    fileId: attachment.fileId,
    role: attachment.roleCode,
    roleCode: attachment.roleCode,
    sortOrder: attachment.sortOrder,
    title: attachment.title,
    altText: attachment.altText,
    caption: attachment.caption,
    publish: attachment.publish,
    publishRequestedAt: attachment.publishRequestedAt,
    publicDeliveryStatus: attachment.publicDeliveryStatus,
    file: toAttachmentFileViewModel(attachment.file),
  };
}

function toEmbeddedAttachmentViewModel(attachment: MediaAttachmentDto): AttachmentViewModel {
  return {
    attachmentId: attachment.attachmentId,
    fileId: attachment.fileId,
    role: attachment.role,
    roleCode: attachment.role,
    sortOrder: attachment.sortOrder,
    title: attachment.title,
    altText: attachment.altText,
    caption: attachment.caption,
    publish: attachment.publish,
    publishRequestedAt: attachment.publishRequestedAt,
    publicDeliveryStatus: attachment.publicDeliveryStatus,
    file: toAttachmentFileViewModel(attachment.file),
  };
}

function toAttachmentFileViewModel(file: MediaAttachmentFileDto): AttachmentFileViewModel {
  return {
    type: file.type,
    status: file.status,
    originalFileName: file.originalFileName,
    contentType: file.contentType,
    sizeBytes: file.sizeBytes,
    widthPx: file.widthPx,
    heightPx: file.heightPx,
    etag: file.etag,
    previewUrl: file.previewUrl,
    renditions: file.renditions.map(toRenditionViewModel),
  };
}

export function toRenditionViewModel(rendition: MediaRenditionDto): RenditionViewModel {
  return {
    kind: rendition.kind,
    fileId: rendition.fileId,
    previewUrl: rendition.previewUrl,
    widthPx: rendition.widthPx,
    heightPx: rendition.heightPx,
    contentType: rendition.contentType,
  };
}
